import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useAccess, useAccountId } from "../Auth/auth-functions";
import { AzureADRoles } from "../Auth/auth-roles";
import { api } from "./api";
import { ListVendorDto, MutableVendorDto, VendorDto } from "../models/vendors.models";

export const useGetVendorsQuery = () => {
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ["vendors", accountId],
		queryFn: async () => {
			let res = await api.get<ListVendorDto[]>("/vendors");
			return res.data;
		},
		staleTime: 5 * 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess
	});
};

export const useGetVendorTypesQuery = () => {
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ['vendorTypes', accountId],
		queryFn: async () => {
			let res = await api.get<string[]>('/vendors/types');
			return res.data;
		},
		staleTime: 5 * 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess
	});
};

export const useGetVendorByIdQuery = (vendorId: string | null) => {
	const hasAccess = useAccess(AzureADRoles.All);
	const accountId = useAccountId();

	return useQuery({
		queryKey: ["vendor", accountId, vendorId],
		queryFn: async () => {
			let res = await api.get<VendorDto>(`/vendors/${vendorId}`);
			return res.data;
		},
		staleTime: 60 * 1000,
		gcTime: 5 * 60 * 1000,
		enabled: hasAccess && vendorId !== "" && vendorId !== "new" && vendorId !== null
	});
};

export const useCreateVendorMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<AxiosResponse<VendorDto>, Error, MutableVendorDto>({
		mutationFn: (model) => {
			return api.post("/vendors", model);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["vendors", accountId] });
		}
	});
};

export const useUpdateVendorMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<AxiosResponse<VendorDto>, Error, MutableVendorDto>({
		mutationFn: (model) => {
			return api.put(`/vendors/${model.VendorId}`, model);
		},
		onSuccess: (_data, model) => {
			queryClient.invalidateQueries({ queryKey: ["vendor", accountId, model.VendorId] });
			queryClient.invalidateQueries({ queryKey: ["vendors", accountId] });
		}
	});
};

export const useDeleteVendorMutation = () => {
	const queryClient = useQueryClient();
	const accountId = useAccountId();

	return useMutation<unknown, Error, VendorDto>({
		mutationFn: (vendor) => {
			return api.delete(`/vendors/${vendor.VendorId}`);
		},
		onSuccess: (_data, vendor) => {
			queryClient.invalidateQueries({ queryKey: ["vendor", accountId, vendor.VendorId] });
			queryClient.invalidateQueries({ queryKey: ["vendors", accountId] });
		}
	});
};
